// src/components/support/SupportChatModal.tsx

import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Sparkles, Trash2, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AIService, ChatMessage } from '../../services/ai.service';

interface SupportChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId?: string;
}

export const SupportChatModal: React.FC<SupportChatModalProps> = ({
  isOpen,
  onClose,
  teacherId = 'guest',
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const storageKey = `support_chat_history_${teacherId}`;

  // Load chat history from localStorage
  useEffect(() => {
    if (isOpen) {
      const savedHistory = localStorage.getItem(storageKey);
      if (savedHistory) {
        try {
          const parsed = JSON.parse(savedHistory);
          setMessages(parsed);
        } catch (err) {
          console.error('Failed to load chat history:', err);
        }
      } else {
        // Show welcome message if no history
        setMessages([
          {
            role: 'model',
            parts: [
              {
                text: "Hi! I'm your AI assistant. I can help you with:\n\n• Understanding app features\n• Troubleshooting issues\n• Answering questions about classroom management\n• Generating educational content\n\nHow can I help you today?",
              },
            ],
          },
        ]);
      }
    }
  }, [isOpen, storageKey]);

  // Save chat history to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      try {
        // Limit to last 20 messages
        const messagesToSave = messages.slice(-20);
        localStorage.setItem(storageKey, JSON.stringify(messagesToSave));
      } catch (err) {
        console.error('Failed to save chat history:', err);
      }
    }
  }, [messages, storageKey]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Close modal on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const modalElement = document.getElementById('support-chat-modal');

      if (modalElement && !modalElement.contains(target)) {
        onClose();
      }
    };

    // Add slight delay to prevent immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setError(null);

    // Add user message
    const newUserMessage: ChatMessage = {
      role: 'user',
      parts: [{ text: userMessage }],
    };

    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Send to AI service
      const response = await AIService.sendChatMessage(userMessage, messages);

      // Add AI response
      const aiMessage: ChatMessage = {
        role: 'model',
        parts: [{ text: response }],
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (err: any) {
      console.error('Chat error:', err);
      setError('Connection lost. Please try again.');

      // Add error message
      const errorMessage: ChatMessage = {
        role: 'model',
        parts: [{ text: "Sorry, I couldn't get a response. Please try again." }],
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        role: 'model',
        parts: [
          {
            text: 'Chat history cleared. How can I help you today?',
          },
        ],
      },
    ]);
    localStorage.removeItem(storageKey);
  };

  const handleGenerateContent = () => {
    navigate('/resources');
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-end z-50 p-4 sm:items-center sm:justify-center">
      <div
        id="support-chat-modal"
        className="bg-white rounded-lg shadow-xl w-full max-w-md h-[600px] flex flex-col relative sm:h-[500px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <h2 className="text-lg font-bold text-gray-800">Support Chat</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearChat}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear chat history"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Generate Content Button */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-100">
          <button
            onClick={handleGenerateContent}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all duration-200 text-sm font-medium group"
            title="Go to resources to generate content"
          >
            <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            Generate Content
          </button>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-none'
                    : 'bg-gray-100 text-gray-800 rounded-bl-none'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">{msg.parts[0].text}</p>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg rounded-bl-none px-4 py-2 flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin text-gray-600" />
                <span className="text-sm text-gray-600">AI is typing...</span>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex justify-center">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value.slice(0, 500))}
              onKeyPress={handleKeyPress}
              placeholder="Ask for help..."
              maxLength={500}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-1 text-xs text-gray-400 text-right">{inputMessage.length}/500</div>
        </div>
      </div>
    </div>
  );
};
